import React from 'react';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { Toolbar } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FavoriteBorder, ListTwoTone } from '@material-ui/icons';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import { useTranslate } from '@sprinx/react-globalize';
import Page from '../../@sprinx/react-web/Page';
import { getFavoriteProducts } from '../../api/products/favoriteProducts';
import CataloguePageListContent from '../CataloguePage/components/CataloguePageListContent';
import CataloguePageListContentSkeleton from '../CataloguePage/components/CataloguePageListContentSkeleton';
import CataloguePageListToolbarViewSwitcher from '../CataloguePage/components/CataloguePageListToolbarViewSwitcher';
import PageHeadline from '../../@sprinx/react-web/PageHeadline';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import StyledBreadcrumb from '../../components/StyledBreadcrumb';
import useRouteUrlFactory from '../../hooks/useRouteUrlFactory';
import { CatalogueProduct } from '../../api/products/catalogue';
import { MoleculerListResult } from '../../@sprinx/knihovka-types';
import usePromise from '../../hooks/usePromise';
import {
  apiClientState,
  currencyState,
  decimalPrecisionState,
  localeState,
  pricesTypeState,
  regionState,
} from '../../api/appState';

export type FavoriteProductsPageLayoutProps = StandardDivProps<FavoriteProductsPageLayoutClassKey>;

export type FavoriteProductsPageLayoutClassKey = 'root' | 'list' | 'toolbar';

const themeSettings = { name: 'FavoriteProductsPageLayout' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<FavoriteProductsPageLayoutClassKey, {}>({
      root: {},
      list: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
      },
      toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(0, 1),
        },
      },
    }),
  themeSettings,
);

const FavoriteProductsPageLayout = React.forwardRef<HTMLDivElement, FavoriteProductsPageLayoutProps>(
  ({ className, classes: pClasses, ...rest }, ref) => {
    const t = useTranslate();
    const routeUrlFactory = useRouteUrlFactory();
    const classes = useStyles({ classes: pClasses });

    const apiClient = useRecoilValue(apiClientState);
    const locale = useRecoilValue(localeState);
    const currency = useRecoilValue(currencyState);
    const decimalPrecision = useRecoilValue(decimalPrecisionState);
    const pricesType = useRecoilValue(pricesTypeState);
    const region = useRecoilValue(regionState);

    const res = usePromise<MoleculerListResult<CatalogueProduct>, unknown>(() =>
      getFavoriteProducts(apiClient, {
        locale,
        currency,
        decimalPrecision,
        enabledDynamicRules: undefined,
        pricesType,
        region,
      }),
    ); // recoil does not work in more windows...

    return (
      <Page
        ref={ref}
        className={clsx(classes.root, className)}
        pageTitle={t('favoriteProductsPage/title')}
        breadcrumbs={
          <AppBreadcrumbs disableHome>
            <StyledBreadcrumb
              to={routeUrlFactory('catalogue')}
              label={t('cataloguePage/title')}
              icon={<ListTwoTone fontSize='small' />}
            />
            <StyledBreadcrumb
              to={routeUrlFactory('favoriteProducts')}
              label={t('favoriteProductsPage/title')}
              icon={<FavoriteBorder fontSize='small' />}
            />
          </AppBreadcrumbs>
        }
        {...rest}
      >
        <PageHeadline ref={ref}>{t('favoriteProductsPage/title')}</PageHeadline>
        <div className={classes.list}>
          <Toolbar ref={ref} className={classes.toolbar}>
            <CataloguePageListToolbarViewSwitcher />
          </Toolbar>
          {res.isLoading && <CataloguePageListContentSkeleton />}
          {res.data && <CataloguePageListContent catalogue={res.data} />}
        </div>
      </Page>
    );
  },
);

FavoriteProductsPageLayout.displayName = 'FavoriteProductsPageLayout';

export default withThemeProps(themeSettings)(FavoriteProductsPageLayout);
